import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppModal from '../components/AppModal';
import api from '../services/api';
import {
  abrirPdfFirmado,
  abrirPdfRecibo,
  obtenerUrlPdfFirmado,
  obtenerUrlPdfRecibo
} from '../utils/abrirDocumento';
import './DetalleReciboPage.css';

function formatoFecha(fecha) {
  if (!fecha) return '-';

  const texto = String(fecha)
    .replace('T', ' ')
    .replace('Z', '')
    .trim();

  const [fechaParte] = texto.split(' ');
  const partes = fechaParte.split('-');

  if (partes.length !== 3) return fechaParte;

  const [anio, mes, dia] = partes;

  return `${dia}/${mes}/${anio}`;
}

function formatoFechaHora(fecha) {
  if (!fecha) return '-';

  const texto = String(fecha)
    .replace('T', ' ')
    .replace('Z', '')
    .trim();

  const [fechaParte, horaParteCompleta = '00:00:00'] = texto.split(' ');
  const partesFecha = fechaParte.split('-');

  if (partesFecha.length !== 3) {
    return formatoFecha(fecha);
  }

  const [anio, mes, dia] = partesFecha;

  const horaLimpia = horaParteCompleta.split('.')[0];
  const partesHora = horaLimpia.split(':');

  const hora = Number(partesHora[0] || 0);
  const minuto = Number(partesHora[1] || 0);

  if (Number.isNaN(hora) || Number.isNaN(minuto)) {
    return `${dia}/${mes}/${anio}`;
  }

  const ampm = hora >= 12 ? 'p.m.' : 'a.m.';
  const hora12 = hora % 12 || 12;

  return `${dia}/${mes}/${anio}, ${String(hora12).padStart(2, '0')}:${String(minuto).padStart(2, '0')} ${ampm}`;
}

function obtenerCodigoMoneda(moneda) {
  const texto = String(moneda || '').toUpperCase();

  if (
    texto.includes('USD') ||
    texto.includes('DOLAR') ||
    texto.includes('DÓLAR')
  ) {
    return 'USD';
  }

  return 'MXN';
}

function formatoImporte(importe, moneda) {
  const numero = Number(importe || 0);
  const codigoMoneda = obtenerCodigoMoneda(moneda);

  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: codigoMoneda,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numero);
}

function obtenerEstatus(recibo) {
  if (!recibo) return 'Emitido';

  if (recibo.Cancelado) return 'Cancelado';

  return recibo.Estatus || 'Emitido';
}

function valorTexto(valor) {
  if (
    valor === null ||
    valor === undefined ||
    String(valor).trim() === ''
  ) {
    return '-';
  }

  return valor;
}

export default function DetalleReciboPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [recibo, setRecibo] = useState(null);
  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState('');
  const [archivoFirmado, setArchivoFirmado] = useState(null);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [notasRecibo, setNotasRecibo] = useState('');
  const [documentoPreviewUrl, setDocumentoPreviewUrl] = useState('');
  const [guardandoFirmado, setGuardandoFirmado] = useState(false);
  const [guardandoNotas, setGuardandoNotas] = useState(false);
  const [avisoModal, setAvisoModal] = useState({
    abierto: false,
    mensaje: '',
    variante: 'info'
  });
  const [confirmarCancelacionModal, setConfirmarCancelacionModal] = useState(false);

  const cargarBitacora = useCallback(async () => {
    const response = await api.get(`/recibos/${id}/bitacora`);
    setBitacora(response.data || []);
  }, [id]);

  const cargarDetalle = useCallback(async () => {
    try {
      setLoading(true);

      const [detalleRes, bitacoraRes] = await Promise.all([
        api.get(`/recibos/${id}`),
        api.get(`/recibos/${id}/bitacora`)
      ]);

      setRecibo(detalleRes.data);
      setNotasRecibo(detalleRes.data?.NotasRecibo || '');
      setBitacora(bitacoraRes.data || []);
    } catch (error) {
      console.error(error);

      setMensaje(
        error?.response?.data?.mensaje ||
        error?.response?.data?.message ||
        'No fue posible cargar el detalle del recibo.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargarDetalle();
  }, [cargarDetalle]);

  useEffect(() => {
    async function cargarDocumentoPreview() {
      if (!recibo?.Id) {
        setDocumentoPreviewUrl('');
        return;
      }

      try {
        const url = recibo.PdfFirmadoPath
          ? await obtenerUrlPdfFirmado(recibo.Id)
          : await obtenerUrlPdfRecibo(recibo.Id);

        setDocumentoPreviewUrl(url);
      } catch {
        setDocumentoPreviewUrl('');
      }
    }

    cargarDocumentoPreview();
  }, [recibo?.Id, recibo?.PdfFirmadoPath]);

  function mostrarAviso(mensajeAviso, variante = 'info') {
    setAvisoModal({
      abierto: true,
      mensaje: mensajeAviso,
      variante
    });
  }

  function cerrarAviso() {
    setAvisoModal({
      abierto: false,
      mensaje: '',
      variante: 'info'
    });
  }

  async function subirFirmado() {
    if (!archivoFirmado) {
      mostrarAviso('Seleccione un archivo PDF.', 'warning');
      return;
    }

    if (!recibo?.Id || recibo?.Cancelado) return;

    try {
      setGuardandoFirmado(true);
      setMensaje('');

      const formData = new FormData();
      formData.append('archivo', archivoFirmado);

      await api.post(
        `/recibos/${recibo.Id}/firmado/${recibo.Folio}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      mostrarAviso(
        recibo?.PdfFirmadoPath
          ? 'Archivo firmado reemplazado correctamente.'
          : 'Archivo firmado cargado correctamente.',
        'success'
      );

      setArchivoFirmado(null);
      await cargarDetalle();
    } catch (error) {
      console.error(error);

      mostrarAviso(
        error?.response?.data?.mensaje ||
        error?.response?.data?.message ||
        'Error al subir archivo firmado.',
        'danger'
      );
    } finally {
      setGuardandoFirmado(false);
    }
  }

  async function cancelarRecibo() {
    if (!motivoCancelacion.trim()) {
      mostrarAviso('Capture el motivo de cancelación.', 'warning');
      return;
    }

    setConfirmarCancelacionModal(true);
  }

  async function confirmarCancelacionRecibo() {
    try {
      setConfirmarCancelacionModal(false);
      setMensaje('');

      await api.put(`/recibos/${id}/cancelar`, {
        motivo: motivoCancelacion
      });

      mostrarAviso('Recibo cancelado correctamente.', 'success');

      setMotivoCancelacion('');
      await cargarDetalle();
    } catch (error) {
      console.error(error);

      mostrarAviso(
        error?.response?.data?.mensaje ||
        error?.response?.data?.message ||
        'Error al cancelar recibo.',
        'danger'
      );
    }
  }

  async function guardarNotasRecibo() {
    if (!recibo?.Id) return;

    try {
      setGuardandoNotas(true);
      setMensaje('');

      const response = await api.put(`/recibos/${recibo.Id}/notas`, {
        NotasRecibo: notasRecibo
      });

      setMensaje(
        response.data?.mensaje ||
        'Notas del recibo guardadas correctamente.'
      );

      setRecibo((prev) => ({
        ...prev,
        NotasRecibo: notasRecibo
      }));

      await cargarBitacora();
    } catch (error) {
      console.error(error);

      setMensaje(
        error?.response?.data?.mensaje ||
        error?.response?.data?.message ||
        error.message ||
        'No fue posible guardar las notas del recibo.'
      );
    } finally {
      setGuardandoNotas(false);
    }
  }

  if (loading) {
    return (
      <div className="detalle-recibo-page">
        <p>Cargando detalle...</p>
      </div>
    );
  }

  if (!recibo) {
    return (
      <div className="detalle-recibo-page">
        <p className="detalle-error">
          {mensaje || 'No se encontró el recibo.'}
        </p>
      </div>
    );
  }

  const estatus = obtenerEstatus(recibo);
  const tieneFirmado = Boolean(recibo.PdfFirmadoPath);
  const reciboCancelado = Boolean(recibo.Cancelado);

  const tituloInformacion = recibo.TipoRecibo
    ? `Recibo de ${recibo.TipoRecibo}`
    : 'Recibo';

  return (
    <div className="detalle-recibo-page">

      <div className="detalle-header">
        <div>
          <h1>Detalle del recibo</h1>
          <p>Consulta general, documentos y bitácora del movimiento</p>
        </div>

        <div className="detalle-header-acciones">
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={() => navigate(-1)}
          >
            Regresar
          </button>

          <button
            className="btn btn-primary"
            type="button"
            onClick={() => navigate('/recibos')}
          >
            Nuevo recibo
          </button>
        </div>
      </div>

      {mensaje && (
        <div className="alert alert-info">
          {mensaje}
        </div>
      )}

      <div className="detalle-grid">

        <div className="detalle-card detalle-card-info">

          <div className="detalle-card-header">
            <h2>{tituloInformacion}</h2>

            <span
              className={
                reciboCancelado
                  ? 'badge badge-cancelado'
                  : 'badge badge-activo'
              }
            >
              {estatus}
            </span>
          </div>

          <div className="detalle-info-grid">
            <div className="detalle-dato">
              <label>Folio:</label>
              <strong>{valorTexto(recibo.Folio)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Fecha:</label>
              <strong>{formatoFecha(recibo.FechaTexto || recibo.FechaCreacion)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Empresa emite:</label>
              <strong>{valorTexto(recibo.EmpresaEmite)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Persona emite:</label>
              <strong>{valorTexto(recibo.PersonaEmite)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Empresa recibe:</label>
              <strong>{valorTexto(recibo.EmpresaRecibe)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Persona recibe:</label>
              <strong>{valorTexto(recibo.PersonaRecibe)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Concepto:</label>
              <strong>{valorTexto(recibo.Concepto)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Importe:</label>
              <strong>{formatoImporte(recibo.Importe, recibo.Moneda)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Moneda:</label>
              <strong>{valorTexto(recibo.Moneda)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Forma de pago:</label>
              <strong>{valorTexto(recibo.FormaPago)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Número de referencia:</label>
              <strong>{valorTexto(recibo.NumeroReferencia)}</strong>
            </div>

            <div className="detalle-dato">
              <label>Vigencia:</label>
              <strong>
                {recibo.FechaInicio || recibo.FechaFin
                  ? `${formatoFecha(recibo.FechaInicio)} - ${formatoFecha(recibo.FechaFin)}`
                  : '-'}
              </strong>
            </div>

            <div className="detalle-dato">
              <label>Observaciones:</label>
              <strong>{valorTexto(recibo.Observaciones)}</strong>
            </div>
          </div>
        </div>

        <div className="detalle-card detalle-documentos">

          <h2>Documentos</h2>

          <div className="detalle-pdf-preview-wrap">
            {documentoPreviewUrl ? (
              <iframe
                src={`${documentoPreviewUrl}#page=1&zoom=page-fit&pagemode=none`}
                title="Vista previa PDF"
                className="detalle-pdf-preview"
              />
            ) : (
              <div className="detalle-pdf-preview d-flex align-items-center justify-content-center text-muted">
                Documento no disponible para vista previa.
              </div>
            )}
          </div>

          <div className="acciones-documentos">
            <button
              className="btn btn-primary"
              type="button"
              onClick={async () => {
                try {
                  await abrirPdfRecibo(recibo.Id);
                } catch (error) {
                  mostrarAviso(
                    error.response?.data?.mensaje ||
                    'No fue posible abrir el PDF.',
                    'danger'
                  );
                }
              }}
            >
              Ver PDF
            </button>

            <button
              className="btn btn-outline-primary"
              type="button"
              disabled={!tieneFirmado}
              onClick={async () => {
                if (!tieneFirmado) return;

                try {
                  await abrirPdfFirmado(recibo.Id);
                } catch (error) {
                  mostrarAviso(
                    error.response?.data?.mensaje ||
                    'No fue posible abrir el PDF firmado.',
                    'danger'
                  );
                }
              }}
            >
              Ver firmado
            </button>
          </div>

          <div className="upload-firmado">

            {reciboCancelado && (
              <div
                className="alert alert-danger"
                style={{
                  marginBottom: '12px',
                  fontSize: '14px'
                }}
              >
                Este recibo está cancelado.
                No es posible reemplazar documentos.
              </div>
            )}

            <label>
              {tieneFirmado
                ? 'Reemplazar PDF firmado'
                : 'Subir PDF firmado'}
            </label>

            <input
              type="file"
              accept="application/pdf"
              disabled={reciboCancelado}
              onChange={(event) =>
                setArchivoFirmado(event.target.files[0] || null)
              }
            />

            <button
              className="btn btn-primary"
              type="button"
              onClick={subirFirmado}
              disabled={
                !archivoFirmado ||
                guardandoFirmado ||
                reciboCancelado
              }
            >
              {guardandoFirmado
                ? 'Guardando...'
                : tieneFirmado
                  ? 'Reemplazar firmado'
                  : 'Subir firmado'}
            </button>
          </div>
        </div>

        <div className="detalle-card detalle-cancelacion">

          <h2>Cancelación</h2>

          <label>Motivo de cancelación</label>

          <textarea
            rows="5"
            disabled={reciboCancelado}
            value={motivoCancelacion}
            onChange={(event) =>
              setMotivoCancelacion(event.target.value)
            }
            placeholder="Captura el motivo de cancelación..."
          />

          {!reciboCancelado ? (
            <button
              className="btn btn-danger detalle-cancelar-btn"
              type="button"
              onClick={cancelarRecibo}
            >
              Cancelar recibo
            </button>
          ) : (
            <div
              className="alert alert-danger mb-0"
              style={{
                fontSize: '14px'
              }}
            >
              Recibo cancelado.
            </div>
          )}

          <div className="detalle-notas-recibo">
            <label>Notas del recibo</label>

            <textarea
              value={notasRecibo}
              onChange={(event) => setNotasRecibo(event.target.value)}
              rows={6}
              placeholder="Captura notas adicionales."
            />

            <button
              className="btn btn-primary"
              type="button"
              onClick={guardarNotasRecibo}
              disabled={guardandoNotas}
            >
              {guardandoNotas ? 'Guardando...' : 'Guardar nota'}
            </button>
          </div>
        </div>

        <div className="detalle-card detalle-bitacora">

          <div className="detalle-card-header">
            <h2>Bitácora</h2>

            <button
              className="btn btn-outline-secondary btn-sm"
              type="button"
              onClick={cargarDetalle}
            >
              Actualizar
            </button>
          </div>

          <div className="bitacora-tabla-wrap">
            <table className="bitacora-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Acción</th>
                  <th>Descripción</th>
                  <th>Usuario</th>
                </tr>
              </thead>

              <tbody>
                {bitacora.length === 0 && (
                  <tr>
                    <td colSpan="4" className="texto-muted">
                      Sin movimientos registrados.
                    </td>
                  </tr>
                )}

                {bitacora.map((item) => (
                  <tr key={item.Id}>
                    <td className="fecha">{formatoFechaHora(item.Fecha)}</td>
                    <td className="movimiento">{item.Accion || '-'}</td>
                    <td>{item.Descripcion || '-'}</td>
                    <td className="ejecuto">{item.Usuario || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <AppModal
        abierto={confirmarCancelacionModal}
        titulo="Sistema Recibos"
        mensaje="¿Seguro que deseas cancelar este recibo? Esta acción quedará registrada en bitácora."
        variante="danger"
        textoCancelar="Cancelar"
        textoAceptar="Cancelar recibo"
        onCancelar={() => setConfirmarCancelacionModal(false)}
        onAceptar={confirmarCancelacionRecibo}
      />

      <AppModal
        abierto={avisoModal.abierto}
        titulo="Sistema Recibos"
        mensaje={avisoModal.mensaje}
        variante={avisoModal.variante}
        mostrarCancelar={false}
        textoAceptar="Aceptar"
        onCancelar={cerrarAviso}
        onAceptar={cerrarAviso}
      />
    </div>
  );
}
